import { apiService, UPLOAD_TIMEOUT } from '@/shared/services/api';
import { endpoints } from '@/shared/services/endpoints';
import { useAuthStore } from '@/shared/stores/auth-store';
import type {
  Campaign,
  CampaignCreative,
  CampaignMetrics,
  DailyMetric,
  AdAccount,
  AiSuggestion,
} from '@/shared/types/entities';
import type { CreativeType } from '@/shared/types/enums';
import type { ApiResponse, PaginatedResponse } from '@/shared/types/api';
import { getTargetingCapabilities } from '../logic/platform-constraints';
import type {
  EventOption,
  LocationTarget,
  InterestTarget,
  AudienceEstimate,
  AudienceEstimateRequest,
  CampaignWizardFormData,
  GeneratedCreativeImage,
} from '../types/campaigns.types';

interface BackendPaginatedResponse<T> {
  data: T[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

interface BackendTargeting {
  id: string;
  locations: unknown;
  ageMin: number | null;
  ageMax: number | null;
  genders: string[];
  // `type` discriminates Google USER_INTEREST/AUDIENCE and Meta BEHAVIOR
  // entries — behaviors persist in the same Json as interests.
  interests: { id: string; name: string; type?: string }[];
  keywords: string[];
}

interface BackendCarouselCard {
  // GET serializes the storage key as `image`; the create payload sends
  // `imageStorageKey` — accept both so the mapper survives either shape.
  image?: string | null;
  imageStorageKey?: string | null;
  headline?: string | null;
  description?: string | null;
  destinationUrl?: string | null;
}

interface BackendCreative {
  id: string;
  type: string;
  headline: string | null;
  body: string | null;
  headlines?: string[] | null;
  descriptions?: string[] | null;
  callToAction: string | null;
  imageUrl: string | null;
  imageStorageKey: string | null;
  /** Landscape image pool (storage keys or URLs); imageUrl mirrors pool[0]. */
  imageUrls?: string[] | null;
  /** Square (1:1) image pool — Google Display campaigns. */
  squareImageUrls?: string[] | null;
  videoUrl: string | null;
  destinationUrl: string | null;
  carouselCards?: BackendCarouselCard[] | null;
  isActive: boolean;
}

interface BackendCampaign {
  id: string;
  organizationId: string;
  adAccountId: string;
  eventId: string | null;
  name: string;
  platform: Campaign['platform'];
  objective: Campaign['objective'];
  status: Campaign['status'];
  managementType?: Campaign['managementType'];
  platformCampaignId?: string | null;
  effectiveStatus?: string | null;
  rejectionReason?: string | null;
  lastStatusSyncAt?: string | null;
  dailyBudgetCents: string;
  totalBudgetCents: string;
  spentCents: string;
  startDate: string;
  endDate: string;
  targeting?: BackendTargeting | null;
  creatives?: BackendCreative[];
  createdAt: string;
  updatedAt: string;
}

/** Shape returned by `POST /v1/campaigns/:id/sync-status`. */
export interface SyncStatusResponse {
  id: string;
  status: Campaign['status'];
  effectiveStatus: string | null;
  rejectionReason: string | null;
  lastStatusSyncAt: string | null;
  changed: boolean;
}

export interface ValidateLandingResponse {
  valid: boolean;
  errors: string[];
}

interface BackendAdAccount {
  id: string;
  platform: AdAccount['platform'];
  platformAccountId: string;
  accountName: string;
  isActive: boolean;
  isManaged?: boolean;
  createdAt: string;
}

interface AssetAccessResponse {
  url: string;
  expiresAt: string;
}

export interface MetaPage {
  id: string;
  name: string;
  category?: string;
}

export type MetaPageListEmptyReason = 'MISSING_SCOPE' | 'NO_PAGES';

export interface MetaPageList {
  pages: MetaPage[];
  reason?: MetaPageListEmptyReason;
  reconnectUrl?: string;
  createUrl?: string;
}

/** A published Page post the organizer can promote as-is. */
export interface MetaPagePost {
  /** `{page-id}_{post-id}` — sent back as the campaign's `boostPostId`. */
  id: string;
  message?: string;
  createdTime?: string;
  permalinkUrl?: string;
  imageUrl?: string;
}

export type MetaPagePostListEmptyReason = 'MISSING_SCOPE' | 'NO_POSTS';

export interface MetaPagePostList {
  posts: MetaPagePost[];
  reason?: MetaPagePostListEmptyReason;
  reconnectUrl?: string;
}

function mapCampaign(campaign: BackendCampaign): Campaign {
  const totalBudgetCents = Number(campaign.totalBudgetCents);
  const dailyBudgetCents = Number(campaign.dailyBudgetCents);

  return {
    id: campaign.id,
    orgId: campaign.organizationId,
    name: campaign.name,
    platform: campaign.platform,
    objective: campaign.objective,
    status: campaign.status,
    // Fall back to SELF_MANAGED for historical rows — the backend added this
    // field later, so legacy payloads may not carry it yet.
    managementType: campaign.managementType ?? 'SELF_MANAGED',
    eventId: campaign.eventId ?? undefined,
    adAccountId: campaign.adAccountId,
    platformCampaignId: campaign.platformCampaignId ?? null,
    effectiveStatus: campaign.effectiveStatus ?? null,
    rejectionReason: campaign.rejectionReason ?? null,
    lastStatusSyncAt: campaign.lastStatusSyncAt ?? null,
    budgetCents: totalBudgetCents || dailyBudgetCents,
    spentCents: Number(campaign.spentCents) || 0,
    budgetType: totalBudgetCents > 0 ? 'total' : 'daily',
    currency: 'USD',
    startDate: campaign.startDate,
    endDate: campaign.endDate,
    targetingConfig: campaign.targeting
      ? {
          locations: Array.isArray(campaign.targeting.locations)
            ? campaign.targeting.locations
            : [],
          ageMin: campaign.targeting.ageMin ?? 18,
          ageMax: campaign.targeting.ageMax ?? 65,
          genders: campaign.targeting.genders ?? [],
          // Behaviors persist alongside interests with type: 'BEHAVIOR' —
          // split on read so the UI renders them as separate sections.
          interests: (campaign.targeting.interests ?? []).filter((i) => i.type !== 'BEHAVIOR'),
          behaviors: (campaign.targeting.interests ?? []).filter((i) => i.type === 'BEHAVIOR'),
          keywords: campaign.targeting.keywords ?? [],
        }
      : {},
    creatives: campaign.creatives?.map(
      (c): CampaignCreative => ({
        id: c.id,
        campaignId: campaign.id,
        type: c.type as CreativeType,
        headline: c.headline ?? '',
        description: c.body ?? '',
        callToAction: c.callToAction ?? '',
        mediaUrl: c.imageUrl ?? c.videoUrl ?? undefined,
        // Full image pool (hydrated to signed URLs by the backend); falls back
        // to the mirrored single image for pre-pool rows.
        mediaUrls: (() => {
          const pool = [...(c.imageUrls ?? []), ...(c.squareImageUrls ?? [])];
          if (pool.length > 0) return pool;
          return c.imageUrl ? [c.imageUrl] : undefined;
        })(),
        thumbnailUrl: c.imageUrl ?? undefined,
        destinationUrl: c.destinationUrl ?? undefined,
        carouselCards: c.carouselCards?.length
          ? c.carouselCards.flatMap((card) => {
              const imageStorageKey = card.image ?? card.imageStorageKey;
              if (!imageStorageKey) return [];
              return [
                {
                  imageStorageKey,
                  headline: card.headline ?? undefined,
                  description: card.description ?? undefined,
                  destinationUrl: card.destinationUrl ?? undefined,
                },
              ];
            })
          : undefined,
        platformCreativeId: undefined,
        status: c.isActive ? ('active' as const) : ('draft' as const),
        createdAt: campaign.createdAt,
        updatedAt: campaign.updatedAt,
      }),
    ),
    createdAt: campaign.createdAt,
    updatedAt: campaign.updatedAt,
  };
}

function mapAdAccount(account: BackendAdAccount, organizationId: string): AdAccount {
  return {
    id: account.id,
    orgId: organizationId,
    platform: account.platform,
    platformAccountId: account.platformAccountId,
    accountName: account.accountName,
    status: account.isActive ? 'active' : 'disconnected',
    tokenExpiresAt: null,
    lastSyncedAt: account.createdAt,
    tokenStatus: 'valid',
    isManaged: account.isManaged ?? false,
    createdAt: account.createdAt,
    updatedAt: account.createdAt,
  };
}

// ---- Campaign CRUD ----

export async function fetchCampaigns(
  filters?: Record<string, unknown>,
): Promise<PaginatedResponse<Campaign>> {
  const { data } = await apiService.get<BackendPaginatedResponse<BackendCampaign>>(
    endpoints.campaigns.list(),
    {
      params: {
        organizationId: filters?.organizationId,
        page: filters?.page,
        limit: filters?.limit,
        status: filters?.status,
        platform: filters?.platform,
      },
    },
  );

  return {
    data: data.data.map(mapCampaign),
    total: data.meta.total,
    page: data.meta.page,
    pageSize: data.meta.limit,
    totalPages: data.meta.totalPages,
  };
}

export async function fetchCampaign(id: string): Promise<Campaign> {
  const { data } = await apiService.get<ApiResponse<BackendCampaign>>(
    endpoints.campaigns.detail(id),
  );
  return mapCampaign(data.data);
}

export async function createCampaign(payload: Partial<Campaign>): Promise<Campaign> {
  const { data } = await apiService.post<ApiResponse<BackendCampaign>>(
    endpoints.campaigns.create(),
    payload,
  );
  return mapCampaign(data.data);
}

/**
 * Shape accepted by `PUT /campaigns/:id`. Mirrors the backend
 * `UpdateCampaignDto` — only fields that DRAFT/PAUSED campaigns are allowed
 * to change. Kept separate from the aggregated {@link Campaign} entity
 * because the edit endpoint takes discrete budget fields and a nested
 * targeting sub-object, not the consumer-facing shape.
 */
export interface UpdateCampaignPayload {
  name?: string;
  dailyBudgetCents?: number;
  totalBudgetCents?: number;
  startDate?: string;
  endDate?: string;
  targeting?: {
    locations?: unknown;
    ageMin?: number;
    ageMax?: number;
    genders?: string[];
    interests?: { id: string; name: string; type?: string }[];
    keywords?: string[];
  };
  pixelId?: string;
  conversionEvent?: string;
  landingUrl?: string;
  version?: number;
}

export async function updateCampaign(
  id: string,
  payload: UpdateCampaignPayload,
): Promise<Campaign> {
  const { data } = await apiService.put<ApiResponse<BackendCampaign>>(
    endpoints.campaigns.update(id),
    payload,
  );
  return mapCampaign(data.data);
}

export async function deleteCampaign(id: string): Promise<void> {
  await apiService.delete(endpoints.campaigns.delete(id));
}

export async function duplicateCampaign(id: string): Promise<Campaign> {
  const { data } = await apiService.post<ApiResponse<BackendCampaign>>(
    endpoints.campaigns.duplicate(id),
  );
  return mapCampaign(data.data);
}

export async function submitCampaignForReview(id: string): Promise<Campaign> {
  const { data } = await apiService.post<ApiResponse<BackendCampaign>>(
    endpoints.campaigns.submitForReview(id),
  );
  return mapCampaign(data.data);
}

export async function submitCampaign(id: string): Promise<Campaign> {
  const { data } = await apiService.post<ApiResponse<BackendCampaign>>(
    endpoints.campaigns.submit(id),
  );
  return mapCampaign(data.data);
}

export async function pauseCampaign(id: string): Promise<Campaign> {
  const { data } = await apiService.post<ApiResponse<BackendCampaign>>(
    endpoints.campaigns.pause(id),
  );
  return mapCampaign(data.data);
}

export async function resumeCampaign(id: string): Promise<Campaign> {
  const { data } = await apiService.post<ApiResponse<BackendCampaign>>(
    endpoints.campaigns.resume(id),
  );
  return mapCampaign(data.data);
}

/**
 * Force a status sync with the ad platform. Used by the detail page when the
 * user wants an up-to-date read instead of waiting for the scheduled 5-minute
 * poll, and by the wizard's review step right after launch.
 */
export async function syncCampaignStatus(id: string): Promise<SyncStatusResponse> {
  const { data } = await apiService.post<ApiResponse<SyncStatusResponse>>(
    endpoints.campaigns.syncStatus(id),
  );
  return data.data;
}

/** Ask the backend whether a landing URL is acceptable for Meta conversion campaigns. */
export async function validateCampaignLanding(params: {
  id: string;
  landingUrl: string;
}): Promise<ValidateLandingResponse> {
  const { data } = await apiService.post<ApiResponse<ValidateLandingResponse>>(
    endpoints.campaigns.validateLanding(params.id),
    { landingUrl: params.landingUrl },
  );
  return data.data;
}

/**
 * Backend shape returned by `GET /v1/analytics/campaigns/:id`
 * (CampaignMetricsResponseDto): nested totals + time-series. Distinct from the
 * flat consumer-facing {@link CampaignMetrics} entity, so we map it here.
 */
interface BackendCampaignMetricsResponse {
  campaignId: string;
  campaignName: string;
  platform: string;
  totals: {
    impressions: number;
    clicks: number;
    conversions: number;
    spendCents: number;
    revenueCents: number;
    reach: number;
    /** Fraction 0-1 — the UI multiplies by 100 for display. */
    ctr: number;
    /** Cents. */
    cpc: number;
    roas: number;
  };
  timeSeries: {
    date: string;
    impressions: number;
    clicks: number;
    conversions: number;
    spendCents: number;
    revenueCents: number;
  }[];
}

function mapCampaignMetrics(res: BackendCampaignMetricsResponse): CampaignMetrics {
  const { totals, timeSeries } = res;
  const dailyMetrics: DailyMetric[] = timeSeries.map((d) => ({
    date: d.date,
    impressions: d.impressions,
    clicks: d.clicks,
    conversions: d.conversions,
    spendCents: d.spendCents,
    revenueCents: d.revenueCents,
  }));

  return {
    campaignId: res.campaignId,
    impressions: totals.impressions,
    clicks: totals.clicks,
    conversions: totals.conversions,
    spendCents: totals.spendCents,
    ctr: totals.ctr,
    cpc: totals.cpc,
    // Backend totals omit cost-per-acquisition; derive cents-per-conversion.
    cpa: totals.conversions > 0 ? Math.round(totals.spendCents / totals.conversions) : 0,
    roas: totals.roas,
    // events.com treats a conversion as a ticket sale; no separate field upstream.
    ticketsSold: totals.conversions,
    revenueCents: totals.revenueCents,
    dateRange: {
      start: dailyMetrics[0]?.date ?? '',
      end: dailyMetrics[dailyMetrics.length - 1]?.date ?? '',
    },
    dailyMetrics,
  };
}

/**
 * Per-campaign metrics. Hits the analytics endpoint (`/analytics/campaigns/:id`)
 * — NOT `/campaigns/:id/metrics`, which was never implemented on the backend
 * (the old path 404'd; the response shape is `{ totals, timeSeries }`).
 */
export async function fetchCampaignMetrics(id: string): Promise<CampaignMetrics> {
  const { data } = await apiService.get<ApiResponse<BackendCampaignMetricsResponse>>(
    endpoints.analytics.campaign(id),
  );
  return mapCampaignMetrics(data.data);
}

/** Fields the backend accepts on a creative update (DRAFT or PAUSED campaigns only). */
export interface UpdateCreativePayload {
  headline?: string;
  body?: string;
  callToAction?: string;
  destinationUrl?: string;
  imageUrl?: string;
  imageStorageKey?: string;
  videoUrl?: string;
  isActive?: boolean;
}

export async function updateCampaignCreative(params: {
  campaignId: string;
  creativeId: string;
  payload: UpdateCreativePayload;
}): Promise<void> {
  await apiService.put(
    endpoints.creatives.update(params.campaignId, params.creativeId),
    params.payload,
  );
}

// ---- Wizard-specific API calls ----

export async function fetchEvents(organizationId: string): Promise<EventOption[]> {
  const { data } = await apiService.get<ApiResponse<EventOption[]>>(
    `${endpoints.events.list()}?organizationId=${encodeURIComponent(organizationId)}`,
  );
  return data.data;
}

export async function fetchAccountsByPlatform(
  organizationId: string,
  platform: string,
): Promise<AdAccount[]> {
  const { data } = await apiService.get<ApiResponse<BackendAdAccount[]>>(
    endpoints.accounts.byPlatform(organizationId),
  );
  return data.data
    .map((account) => mapAdAccount(account, organizationId))
    .filter((account) => account.platform === platform);
}

export async function saveCampaignDraft(
  payload: Partial<CampaignWizardFormData>,
): Promise<Campaign> {
  // POST /campaigns creates the campaign in DRAFT status — there is no
  // dedicated draft endpoint on the backend.
  const { data } = await apiService.post<ApiResponse<BackendCampaign>>(
    endpoints.campaigns.create(),
    mapWizardPayloadToCreateCampaign(payload),
  );
  return mapCampaign(data.data);
}

export async function updateCampaignDraft(
  id: string,
  payload: Partial<CampaignWizardFormData>,
): Promise<Campaign> {
  // PUT /campaigns/:id only whitelists these fields (UpdateCampaignDto with
  // forbidNonWhitelisted) — creatives can't be updated in place. The launch
  // flow recreates the campaign from the full wizard payload, so creative
  // edits are never lost; only the saved draft record lags behind.
  const mapped = mapWizardPayloadToCreateCampaign(payload);
  const { data } = await apiService.put<ApiResponse<BackendCampaign>>(
    endpoints.campaigns.update(id),
    {
      name: mapped.name,
      dailyBudgetCents: mapped.dailyBudgetCents,
      totalBudgetCents: mapped.totalBudgetCents,
      startDate: mapped.startDate,
      endDate: mapped.endDate,
      targeting: mapped.targeting,
      pixelId: mapped.pixelId,
      conversionEvent: mapped.conversionEvent,
      landingUrl: mapped.landingUrl,
    },
  );
  return mapCampaign(data.data);
}

export async function searchLocations(query: string): Promise<LocationTarget[]> {
  const { data } = await apiService.get<ApiResponse<LocationTarget[]>>(
    endpoints.targeting.locations(query),
  );
  return data.data;
}

export async function searchInterests(query: string): Promise<InterestTarget[]> {
  const { data } = await apiService.get<ApiResponse<InterestTarget[]>>(
    endpoints.targeting.interests(query),
  );
  return data.data;
}

export async function searchBehaviors(query: string): Promise<InterestTarget[]> {
  const { data } = await apiService.get<ApiResponse<InterestTarget[]>>(
    endpoints.targeting.behaviors(query),
  );
  return data.data;
}

export async function fetchAudienceEstimate(
  config: AudienceEstimateRequest,
): Promise<AudienceEstimate> {
  const { data } = await apiService.post<ApiResponse<AudienceEstimate>>(
    endpoints.targeting.audienceEstimate(),
    config,
  );
  return data.data;
}

export async function fetchBudgetSuggestion(params: {
  objective: string;
  platform: string;
  durationDays?: number;
  audienceSize?: number;
  eventDate?: string;
}): Promise<AiSuggestion> {
  const { data } = await apiService.post<ApiResponse<AiSuggestion>>(
    endpoints.ai.budgetSuggestion(),
    params,
  );
  return data.data;
}

export async function fetchCopySuggestion(params: {
  platform: string;
  objective?: string;
  eventName: string;
  eventDescription?: string;
  eventCategory?: string;
  location?: string;
  destinationUrl?: string;
  tone?: string;
  callToAction?: string;
  targetAudience?: string[];
  variations?: number;
}): Promise<AiSuggestion> {
  const { data } = await apiService.post<ApiResponse<AiSuggestion>>(
    endpoints.ai.copySuggestion(),
    params,
  );
  return data.data;
}

/**
 * Upload a user-picked image to S3 via the BE so it can be referenced by
 * storage key on campaign launch. Returns the same shape as AI-generated
 * images so the wizard can treat them interchangeably.
 */
export async function uploadCreativeImage(params: {
  file: File;
  organizationId: string;
}): Promise<GeneratedCreativeImage> {
  const dataBase64 = await readFileAsBase64(params.file);
  // Base64 payloads of full-size images (up to ~6.7MB for a 5MB file) can take
  // longer than the 30s default, so use the extended upload timeout. Body stays
  // JSON — do NOT use uploadConfig() here (it forces multipart/form-data).
  const { data } = await apiService.post<ApiResponse<GeneratedCreativeImage>>(
    endpoints.creativeLibrary.uploadImage(),
    {
      organizationId: params.organizationId,
      mimeType: params.file.type,
      dataBase64,
    },
    { timeout: UPLOAD_TIMEOUT },
  );
  return data.data;
}

/**
 * Upload a creative video straight to S3 via a presigned PUT URL. The bytes
 * never pass through the API pod (the image flow base64s through JSON, which
 * the 10MB body limit caps at ~7MB — too small for real video). Returns the
 * S3 storage key to send on the creative at launch.
 */
export async function uploadCreativeVideo(params: {
  file: File;
  organizationId: string;
}): Promise<{ videoStorageKey: string }> {
  // Step 1: mint a presigned PUT URL (bytes stay off the API pod).
  const { data } = await apiService.post<
    ApiResponse<{ uploadUrl: string; storageKey: string; expiresAt: string }>
  >(endpoints.creativeLibrary.uploadUrl(), {
    organizationId: params.organizationId,
    mimeType: params.file.type,
  });
  const { uploadUrl, storageKey } = data.data;

  // Step 2: PUT the file directly to S3. This is a cross-origin request to the
  // bucket, NOT our API — use fetch, not apiService (no credentials/baseURL).
  const putResponse = await fetch(uploadUrl, {
    method: 'PUT',
    body: params.file,
    headers: { 'Content-Type': params.file.type },
  });
  if (!putResponse.ok) {
    throw new Error(`Video upload failed (S3 returned ${putResponse.status})`);
  }

  return { videoStorageKey: storageKey };
}

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('File read failed'));
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Unexpected FileReader result'));
        return;
      }
      // result format: "data:<mime>;base64,<payload>" — strip the prefix.
      const commaIdx = result.indexOf(',');
      resolve(commaIdx >= 0 ? result.slice(commaIdx + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}

export async function fetchMetaPages(adAccountId: string): Promise<MetaPageList> {
  const { data } = await apiService.get<
    ApiResponse<{
      data: MetaPage[];
      reason?: MetaPageListEmptyReason;
      reconnectUrl?: string;
      createUrl?: string;
    }>
  >(endpoints.accounts.metaPages(adAccountId));
  return {
    pages: data.data?.data ?? [],
    reason: data.data?.reason,
    reconnectUrl: data.data?.reconnectUrl,
    createUrl: data.data?.createUrl,
  };
}

export async function fetchMetaPagePosts(
  adAccountId: string,
  pageId: string,
): Promise<MetaPagePostList> {
  const { data } = await apiService.get<
    ApiResponse<{
      data: MetaPagePost[];
      reason?: MetaPagePostListEmptyReason;
      reconnectUrl?: string;
    }>
  >(endpoints.accounts.metaPagePosts(adAccountId, pageId));
  return {
    posts: data.data?.data ?? [],
    reason: data.data?.reason,
    reconnectUrl: data.data?.reconnectUrl,
  };
}

export async function setDefaultPage(
  adAccountId: string,
  pageId: string,
): Promise<{ id: string; defaultPageId: string }> {
  const { data } = await apiService.put<ApiResponse<{ id: string; defaultPageId: string }>>(
    endpoints.accounts.defaultPage(adAccountId),
    { pageId },
  );
  return data.data;
}

export async function fetchAssetAccessUrl(key: string): Promise<AssetAccessResponse> {
  const { data } = await apiService.get<ApiResponse<AssetAccessResponse>>(
    endpoints.ai.assetAccess(key),
  );
  return data.data;
}

function mapWizardPayloadToCreateCampaign(
  payload: Partial<CampaignWizardFormData>,
): Record<string, unknown> {
  const organizationId = useAuthStore.getState().orgId;

  if (!organizationId) {
    throw new Error('Organization is required to create a campaign');
  }

  // Conversion-objective fields: fall back to `destinationUrl` from the
  // Creative step when the wizard didn't collect a dedicated landingUrl
  // so the backend still receives a valid HTTPS URL.
  const landingUrl = payload.landingUrl ?? payload.destinationUrl ?? undefined;
  const targetingCaps = getTargetingCapabilities(payload.platform);

  return {
    organizationId,
    managementType: payload.managementType ?? 'SELF_MANAGED',
    adAccountId: payload.adAccountId || undefined,
    eventId: payload.eventId,
    name: payload.name,
    platform: payload.platform,
    objective: payload.objective,
    dailyBudgetCents: payload.dailyBudgetCents,
    totalBudgetCents: payload.totalBudgetCents,
    startDate: payload.startDate,
    endDate: payload.endDate,
    pixelId: payload.pixelId,
    conversionEvent: payload.conversionEvent,
    landingUrl,
    boostPostId: payload.boostPostId || undefined,
    targeting: payload.targetingConfig
      ? {
          locations: payload.targetingConfig.locations,
          ageMin: payload.targetingConfig.ageMin,
          ageMax: payload.targetingConfig.ageMax,
          genders: payload.targetingConfig.genders,
          // Send { id, name, type }: `type` routes Google entries to the right
          // criterion (USER_INTEREST vs AUDIENCE) and marks Meta behaviors
          // (BEHAVIOR) — behaviors persist in the same interests Json and the
          // backend splits them before dispatch.
          interests: [
            ...payload.targetingConfig.interests.map((i) => ({
              id: i.id,
              name: i.name,
              ...(i.type ? { type: i.type } : {}),
            })),
            ...(targetingCaps.behaviors
              ? (payload.targetingConfig.behaviors ?? []).map((b) => ({
                  id: b.id,
                  name: b.name,
                  type: 'BEHAVIOR' as const,
                }))
              : []),
          ],
          // Platforms without a keyword concept (Meta) never persist keywords,
          // so stale entries can't survive a platform switch.
          keywords: targetingCaps.keywords ? payload.targetingConfig.keywords : undefined,
        }
      : undefined,
    // Optional on the backend — mid-wizard draft saves happen before the
    // Creative step has run.
    creatives: (payload.creatives ?? []).map((creative) => {
      // CreativeStep initializes with [''] — drop empties so placeholder rows
      // never reach the platforms (Google RSA counts every entry).
      const headlines = creative.headlines.map((h) => h.trim()).filter(Boolean);
      const descriptions = creative.descriptions.map((d) => d.trim()).filter(Boolean);
      // Image pools: every uploaded/AI-generated image ships to the platform,
      // which mixes them freely with the text pools (Google RDA, Meta asset
      // feeds). The singular keys mirror pool[0] for backward compatibility.
      const imageStorageKeys = (creative.generatedImages ?? [])
        .map((img) => img.imageStorageKey)
        .filter(Boolean);
      const squareImageStorageKeys = (creative.squareGeneratedImages ?? [])
        .map((img) => img.imageStorageKey)
        .filter(Boolean);
      return {
        type: creative.type,
        // Singular fields stay the primary copy (Meta primaryText, legacy column).
        headline: headlines[0],
        body: descriptions[0],
        headlines: headlines.length ? headlines : undefined,
        descriptions: descriptions.length ? descriptions : undefined,
        callToAction: creative.callToAction,
        imageStorageKey: imageStorageKeys[0],
        imageStorageKeys: imageStorageKeys.length ? imageStorageKeys : undefined,
        // Square (1:1) images — Google Display reads these as squareMarketingImages.
        squareImageStorageKey: squareImageStorageKeys[0],
        squareImageStorageKeys: squareImageStorageKeys.length ? squareImageStorageKeys : undefined,
        // Video (uploaded via presigned PUT) — sent for VIDEO creatives.
        videoStorageKey: creative.videoStorageKey,
        // Cover image for the video — Meta requires a thumbnail.
        videoThumbnailStorageKey: creative.videoThumbnailStorageKey,
        // YouTube URL — Google VIDEO creatives; backend extracts the video id.
        youtubeVideoUrl: creative.youtubeVideoUrl,
        // Carousel cards — sent for CAROUSEL creatives.
        carouselCards: creative.carouselCards?.length
          ? creative.carouselCards.map((card) => ({
              imageStorageKey: card.imageStorageKey,
              headline: card.headline || undefined,
              description: card.description || undefined,
              destinationUrl: card.destinationUrl || undefined,
            }))
          : undefined,
        destinationUrl: payload.destinationUrl,
      };
    }),
  };
}

export async function launchCampaign(
  payload: CampaignWizardFormData,
  supersededDraftId?: string | null,
): Promise<Campaign> {
  // Step 1: Create the campaign as DRAFT
  const { data } = await apiService.post<ApiResponse<BackendCampaign>>(
    endpoints.campaigns.create(),
    mapWizardPayloadToCreateCampaign(payload),
  );
  const campaign = mapCampaign(data.data);

  let launched: Campaign;
  try {
    // Step 2: For events-managed campaigns, submit for review instead of launching
    if (payload.managementType === 'EVENTS_MANAGED') {
      launched = await submitCampaignForReview(campaign.id);
    } else {
      // Step 3: For self-managed, launch directly
      const { data: launchedData } = await apiService.post<ApiResponse<BackendCampaign>>(
        endpoints.campaigns.submit(campaign.id),
      );
      launched = mapCampaign(launchedData.data);
    }
  } catch (error) {
    // The draft was created but the launch/submit failed. Archive the orphan
    // so each retry doesn't leave another duplicate DRAFT in the campaign
    // list — the wizard keeps the user's data in sessionStorage, and the
    // retry recreates the campaign with the full (possibly edited) payload,
    // which a PUT couldn't carry (UpdateCampaignDto has no creatives).
    await deleteCampaign(campaign.id).catch(() => undefined);
    throw error;
  }

  // The launched campaign supersedes the draft the wizard saved earlier
  // ("Save draft" button) — archive it so the list doesn't keep a stale copy.
  if (supersededDraftId) {
    await deleteCampaign(supersededDraftId).catch(() => undefined);
  }
  return launched;
}
